/**
 * The top of the Files read tab: what the run checked and how much of what it
 * read reached the memo (DOP-623 restyle, in the redesign canvas's look).
 *
 * One card: the verification result in words and the failed checks as a
 * headline and a list. Not how much was read (the list below says it) nor
 * what the run drafted and left out: that is the run's bookkeeping, not the
 * reader's (Turtleback 10-03). Under it, when
 * anything is open, one line pointing at the Gaps tab — the items themselves
 * live there, not twice. The files, one card each, follow (`SourcesView`).
 */

import { stripMarks } from "./inlineMarks";
import { ArrowRight, CircleAlert, CircleCheck } from "lucide-react";
import React from "react";

import { type DealScreeningRun, blockingTotal } from "../../types/dealScreening";
import { BTN_SM, CARD } from "./checkStyles";
import { countLabel } from "./dealScreeningFormat";
import { reviewItems } from "./dealScreeningReview";

export interface RunReportViewProps {
	run: DealScreeningRun;
	onOpenGaps: () => void;
}

const RunReportView: React.FC<RunReportViewProps> = ({ run, onOpenGaps }) => {
	const checks = run.report.checks;
	const failed = checks.filter((check) => !check.passed);
	const items = reviewItems(run);
	const added = run.sections.filter(
		(section) => section.deal_specific && section.status === "written",
	);
	const passed = failed.length === 0;

	return (
		<div>
			<section className={`${CARD} px-5 py-4`} data-testid="checks-line" aria-label="What the run checked">
				<div className="flex items-start gap-3">
					<span
						className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
							passed ? "bg-[#ecfdf3] text-[#067647]" : "bg-[#fffaeb] text-[#b54708]"
						}`}
					>
						{passed ? (
							<CircleCheck className="h-4 w-4" aria-hidden />
						) : (
							<CircleAlert className="h-4 w-4" aria-hidden />
						)}
					</span>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-[#111111]">
							{passed ? "Verification passed" : "Verification found issues"} ·{" "}
							{countLabel(checks.length, "check")} · {countLabel(failed.length, "issue")}
						</p>
						{failed.map((check) => {
							// "9 of 15 computed; missing: A (…); B (…)" reads as a headline
							// and a list, not a wall.
							const [headline, ...rest] = check.detail.split(/;\s+/);
							return (
								<div key={check.name} className="mt-2 text-[13px] leading-normal text-[#7a2e0e]">
									<p>
										<span className="font-semibold">{check.name.replace(/_/g, " ")}</span>
										{headline ? ` — ${headline.replace(/:$/, "")}` : ""}
									</p>
									{rest.length > 0 && (
										<ul className="mt-0.5 list-disc space-y-0.5 pl-4">
											{rest.map((item) => (
												<li key={item}>{item.replace(/^missing:\s*/, "")}</li>
											))}
										</ul>
									)}
								</div>
							);
						})}
						{added.length > 0 && (
							<p className="mt-2 text-[13px] leading-normal text-[#5B6472]">
								Added for this deal: {added.map((section) => stripMarks(section.title)).join(", ")}.
							</p>
						)}
					</div>
				</div>
				{items.length > 0 && (
					<div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-[#fedf89] bg-[#fffaeb] px-3.5 py-2.5">
						<p className="min-w-0 flex-1 text-[13px] font-medium text-[#7a2e0e]">
							{countLabel(items.length, "gap")} open
							{blockingTotal(run) > 0 ? `, ${blockingTotal(run)} blocking` : ""}.
						</p>
						<button type="button" onClick={onOpenGaps} className={BTN_SM}>
							Open Gaps
							<ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden />
						</button>
					</div>
				)}
			</section>

		</div>
	);
};

export default RunReportView;
